import { Checkbox, Input, Tooltip, TooltipContent, TooltipTrigger, cn } from '@design-system';
import { Info } from 'lucide-react';

import { useCallback, useEffect, useState } from 'react';

import { formatCurrency } from '@shared';

export type AllocationOption = 'mirror' | 'split-50-50' | 'custom';

export interface AllocationConfig {
  option: AllocationOption;
  clientPercent: number;
  partnerPercent: number;
}

const PRESET_OPTIONS: {
  value: AllocationOption;
  label: string;
  description: string;
  clientRatio: number;
  partnerRatio: number;
}[] = [
  {
    value: 'mirror',
    label: 'Full Each',
    description: 'Both keep full amount',
    clientRatio: 1,
    partnerRatio: 1,
  },
  {
    value: 'split-50-50',
    label: '50 / 50',
    description: 'Split evenly',
    clientRatio: 0.5,
    partnerRatio: 0.5,
  },
  {
    value: 'custom',
    label: 'Custom',
    description: 'Set your own',
    clientRatio: 0,
    partnerRatio: 0,
  },
];

interface PartnerAllocationSectionProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  value: number;
  clientName: string;
  partnerName: string;
  selectedOption: AllocationOption;
  onOptionChange: (option: AllocationOption) => void;
  customClientPercent: number;
  onCustomClientPercentChange: (percent: number) => void;
  disabled?: boolean;
  className?: string;
}

export function PartnerAllocationSection({
  enabled,
  onEnabledChange,
  value,
  clientName,
  partnerName,
  selectedOption,
  onOptionChange,
  customClientPercent,
  onCustomClientPercentChange,
  disabled,
  className,
}: PartnerAllocationSectionProps) {
  const [customInputValue, setCustomInputValue] = useState(String(customClientPercent));

  useEffect(() => {
    setCustomInputValue(String(customClientPercent));
  }, [customClientPercent]);

  const handleCustomInputChange = useCallback(
    (rawValue: string) => {
      if (rawValue === '') {
        setCustomInputValue('');
        return;
      }
      const num = parseInt(rawValue, 10);
      if (isNaN(num)) return;
      const clamped = Math.min(99, Math.max(1, num));
      setCustomInputValue(String(clamped));
      onCustomClientPercentChange(clamped);
    },
    [onCustomClientPercentChange],
  );

  const handleCustomInputBlur = useCallback(() => {
    if (customInputValue === '' || isNaN(parseInt(customInputValue, 10))) {
      setCustomInputValue(String(customClientPercent));
    }
  }, [customInputValue, customClientPercent]);

  const clientFirst = clientName.split(' ')[0];
  const partnerFirst = partnerName.split(' ')[0];

  return (
    <div className={cn('border-t pb-5 pt-4', className)}>
      <label className="flex cursor-pointer items-start gap-3">
        <Checkbox
          checked={enabled}
          onCheckedChange={checked => onEnabledChange(!!checked)}
          disabled={disabled}
          className="mt-0.5"
        />
        <div>
          <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
            Also add this account to {partnerFirst}&apos;s plan
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="text-muted-foreground size-3.5 cursor-help" />
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs text-xs font-normal"
              >
                Adding this account will split the balance, contributions, and withdrawals across
                both accounts. The split can be 50/50 or set with a custom ratio.
              </TooltipContent>
            </Tooltip>
          </p>
          <p className="text-muted-foreground text-xs">
            This creates the same account in {partnerFirst}&apos;s plan. Future edits will stay in
            sync.
          </p>
        </div>
      </label>

      {enabled && (
        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-gray-950">
            How should the balance be allocated?
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {PRESET_OPTIONS.map(opt => {
              const isSelected = selectedOption === opt.value;
              const clientRatio =
                opt.value === 'custom' ? customClientPercent / 100 : opt.clientRatio;
              const partnerRatio =
                opt.value === 'custom' ? (100 - customClientPercent) / 100 : opt.partnerRatio;
              const clientAmt = opt.value === 'mirror' ? value : Math.round(value * clientRatio);
              const partnerAmt = opt.value === 'mirror' ? value : Math.round(value * partnerRatio);

              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={disabled}
                  onClick={() => onOptionChange(opt.value)}
                  className={cn(
                    'hover:bg-muted rounded-lg border p-2.5 text-start transition-colors',
                    {
                      'border-primary bg-primary/5': isSelected,
                      'cursor-not-allowed opacity-60': disabled,
                    },
                  )}
                >
                  <div className="border-b pb-2">
                    <div
                      className={cn('text-sm font-medium text-gray-950', {
                        'text-primary': isSelected,
                      })}
                    >
                      {opt.label}
                    </div>
                    <div
                      className={cn('text-foreground text-sm', {
                        'text-primary/70': isSelected,
                      })}
                    >
                      {opt.description}
                    </div>
                  </div>

                  <div className="mt-2 space-y-0.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span
                        className={cn('text-foreground text-sm', {
                          'text-primary/70': isSelected,
                        })}
                      >
                        {clientFirst}
                      </span>
                      <span
                        className={cn('font-medium text-gray-950', {
                          'text-primary': isSelected,
                        })}
                      >
                        {formatCurrency(clientAmt)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span
                        className={cn('text-foreground text-sm', {
                          'text-primary/70': isSelected,
                        })}
                      >
                        {partnerFirst}
                      </span>
                      <span
                        className={cn('font-medium text-gray-950', {
                          'text-primary': isSelected,
                        })}
                      >
                        {formatCurrency(partnerAmt)}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedOption === 'custom' && (
            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1">
                <label className="text-foreground mb-1 block text-xs font-medium">
                  {clientFirst}&apos;s share (%)
                </label>
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={customInputValue}
                  onChange={e => handleCustomInputChange(e.target.value)}
                  onBlur={handleCustomInputBlur}
                  disabled={disabled}
                />
              </div>
              <div className="flex-1">
                <label className="text-foreground mb-1 block text-xs font-medium">
                  {partnerFirst}&apos;s share (%)
                </label>
                <Input
                  type="number"
                  value={String(100 - customClientPercent)}
                  disabled
                />
              </div>
            </div>
          )}

          {selectedOption !== 'mirror' && (
            <p className="text-muted-foreground mt-3 text-xs">
              The chosen split applies to the balance, future contributions, and any withdrawals.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
